import { ComparisonSymbolLegendsComponent } from "../components/comparison-symbol-legends.component";
import { ComparisonSymbolsLastPriceComponent } from "../components/comparison-symbols-last-price.component";
import { TiChartSeriesComponent } from "../components/ti-chart-series.component";
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { TraceLoggingHelpers } from "../helpers/trace-logging-helpers";
import { TIDataPointToHighChartDataPointMapper } from "../mappers/data-point/ti-chart-point-to-highchart-data-point.mapper";
import { TIComparisonDataPointToHighChartDataPointMapper } from "../mappers/data-point/ti-comparison-chart-point-to-highchart-data-point.mapper";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIComparisonDataModel } from "../models/data-point/ti-comparison-data.model";
import { TIComparisonHistoryDataModel } from "../models/data-point/ti-comparison-history-data.model";
import { HighChartBuilder } from "./highchart.builder";

interface PrimaryComparisonPointPair {
    primaryDataPoint: TIChartPointModel,
    matchingComparisonDataPoint: TIChartPointModel
}

interface CurrentDisplayValueResult {
    value: string;
    percentageChange: string;
}

export class HighChartComparisonBuilder {

    private id = StringHelpers.newGuid();
    private tiChartSeries: TiChartSeriesComponent;
    private comparisonSymbolLegend: ComparisonSymbolLegendsComponent | null = null;
    private comparisonSymbolsLastPrice: ComparisonSymbolsLastPriceComponent | null = null;
    private firstVisibleXValue: number | null = null;

    private updatingDataPoint = false;

    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.tiChartSeries.subscribeExtremeChange(this.id, () => {
            this.buildComparisonsComponents(0, false);
        });
    }

    public removeComparisonsComponents(removeAllSeries: boolean) {
        this.tiChartSeries.highChartStyleBuilder.createComparisonChartStyle([]);

        if (removeAllSeries) {
            let needRedraw = false;
            const existingSeries = this.tiChartSeries.highStockChart?.series?.filter((x) => x.name.indexOf('comparison-series-') > - 1);
            if (existingSeries?.length) {
                this.tiChartSeries.tiComparisionsData = [];

                for (const serie of existingSeries) {
                    serie.remove(false, false, false);
                    needRedraw = true;
                }
                
                this.getComponents();
                this.comparisonSymbolLegend?.hide();
                this.comparisonSymbolsLastPrice?.hide();
            }

            if (needRedraw) {
                this.redraw();
            }
        }
    }

    private renderComparisonTimer: any;
    public buildComparisonsComponents(retryCount: number, resetLengends: boolean) {
        if (!this.tiChartSeries.ready || !this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart || !this.tiChartSeries.highStockChart.series?.length) {
            setTimeout(() => {
                this.buildComparisonsComponents(retryCount + 1, resetLengends);
            }, 250);
            return;
        }

        if (retryCount >= 100) {
            TraceLoggingHelpers.log('abort build comparison!');
            return;
        }

        clearTimeout(this.renderComparisonTimer);
        this.renderComparisonTimer = setTimeout(() => {
            this.renderInitComparisonSymbols(resetLengends);
        }, 100);
    }

    private renderInitComparisonSymbols(resetLegends: boolean) {
        if (this.updatingDataPoint || !this.tiChartSeries.chartStyleSettings) {
            return;
        }

        this.getComponents();
        this.removeComparisonsComponents(false);
        if (resetLegends) {
            this.comparisonSymbolLegend?.hide();
            this.comparisonSymbolsLastPrice?.hide();
        }

        this.tiChartSeries.highChartStyleBuilder.createComparisonChartStyle(this.tiChartSeries.tiComparisionsData);
        
        let needRedraw = false;

        const lastPriceTemplate = `<div class="last-price-wrapper {class}" style="{style}">
                <div class="last-price">
                    <p>{value}</p>
                </div>
            </div>
            <style>
                .last-price-wrapper.{class} {
                    background-color: var(--{class}-bg-color);
                } 
                .last-price-wrapper.{class} .last-price:before {
                    border-color: transparent var(--{class}-bg-color) transparent transparent;
                }
            </style>`;

        let legendHtml = '';
        let lastPriceHtml = '';
        const root = document.documentElement;

        const percentageMode = this.tiChartSeries.chartStyleSettings.isComparisonSymbolPercentageMode;
        const mainSeries: any = this.tiChartSeries.highStockChart?.series[0];
        const primarySymbol =  this.tiChartSeries.symbol;
        this.firstVisibleXValue = mainSeries && mainSeries.processedXData?.length ? mainSeries.processedXData[1] : null;

        if (mainSeries && percentageMode && this.tiChartSeries.tiComparisionsData.length) {
            const value = this.tiChartSeries.lastDataPoint ? this.getPercentageAnnotationText(this.tiChartSeries.lastDataPoint) : '';
            
            legendHtml = `<p class="${primarySymbol}" data-symbol="${primarySymbol}" style="color:${this.tiChartSeries.chartStyleSettings.selectedLineColor.toRGB()};">${primarySymbol}: ${value}</p>`;
    
            const dataPoints = TIDataPointToHighChartDataPointMapper.map(this.tiChartSeries);
            mainSeries.setData(dataPoints.points, false, false);
            
            needRedraw = true;
        }

        if (this.tiChartSeries.tiComparisionsData.length) {
            const comparisonIds: Array<string> = [];
            for (const comparisonData of this.tiChartSeries.tiComparisionsData) {
                if (!comparisonData.points.length) {
                    continue;
                }

                const id = 'comparison-series-' + comparisonData.symbol;
                comparisonIds.push(id);

                const lastXValue =  comparisonData.points[comparisonData.points.length - 1].xValue;
                
                this.setRatio(comparisonData, this.firstVisibleXValue);
                const dataPoints = TIComparisonDataPointToHighChartDataPointMapper.map(comparisonData, this.tiChartSeries);
                const existingSerie = this.tiChartSeries.highStockChart.series.find((x) => x.name == id);
                if (existingSerie) {
                    existingSerie.setData(dataPoints.points, false, false);
                } else {
                    const config: any = {
                        id: id,
                        name: id,
                        className: id,
                        yAxis: HighChartBuilder.mainChartAreaName + '-y-axis',
                        lineWidth: 1,
                        dataGrouping: {
                            enabled: false
                        },
                        marker: {
                            enabled: false,
                            states: {
                                hover: {
                                    enabled: false
                                }
                            }
                        },
                        states: {
                            inactive: {
                                opacity: 1
                            }
                        },
                        enableMouseTracking: false,
                        data: dataPoints.points
                    };
                    this.tiChartSeries.highStockChart.addSeries(config, false, false);
                }

                needRedraw = true;

                const className = 'comparison-last-price-' + comparisonData.symbol;
                const displayResult = this.getCurrentDisplayValue(comparisonData, lastXValue);
                root.style.setProperty('--' + className + '-bg-color', comparisonData.color.toRGB());

                
                const lastPriceWrapper = this.tiChartSeries.element.findFirst('.last-price-wrapper.comparison-last-price-' + comparisonData.symbol);
                let existStyle = '';
                if (lastPriceWrapper) {
                    existStyle = 'top: ' + lastPriceWrapper.style.top;
                }

                lastPriceHtml += lastPriceTemplate
                    .split('{class}')
                    .join(className)
                    .replace('{value}', `${comparisonData.symbol} ${displayResult.percentageChange}`)
                    .replace('{style}', existStyle);

                legendHtml += `<p class="${comparisonData.symbol}" data-symbol="${comparisonData.symbol}" style="color:${comparisonData.color.toRGB()};">${comparisonData.symbol}: ${displayResult.percentageChange}</p>`
            }

            if (needRedraw) {
                this.tiChartSeries.highStockChart.update({
                    chart: {
                        spacingTop: 66
                    }
                }, false);
            }

            const existingSeries = this.tiChartSeries.highStockChart?.series?.filter((x) => x.name.indexOf('comparison-series-') > - 1 && comparisonIds.indexOf(x.name) == -1);
            if (existingSeries?.length) {
                for (const serie of existingSeries) {
                    serie.remove(false, false, false);
                    needRedraw = true;
                }
            }

            if (this.comparisonSymbolLegend) {
                this.comparisonSymbolLegend.show(legendHtml);
            }

            if (this.comparisonSymbolsLastPrice) {
                this.comparisonSymbolsLastPrice.show(lastPriceHtml);
            }
        } else {
            const existingSeries = this.tiChartSeries.highStockChart?.series?.filter((x) => x.name.indexOf('comparison-series-') > - 1);
            if (existingSeries?.length) {
                for (const serie of existingSeries) {
                    serie.remove(false, false, false);
                    needRedraw = true;
                }

                this.tiChartSeries.highStockChart.update({
                    chart: {
                        spacingTop: 46
                    }
                }, false);
    
                needRedraw = true;
            }

            this.comparisonSymbolLegend?.hide();
            this.comparisonSymbolsLastPrice?.hide();
        }

        if (needRedraw) {
            this.redraw();
        }
    }

    private redraw() {
        this.tiChartSeries.highStockChart.redraw(false);
        setTimeout(() => {
            this.tiChartSeries.setPanel();
            this.tiChartSeries.base.setLastPricePositions();
            this.tiChartSeries.highChartAxisBuilder.buildAxisComponents();
        }, 100);
    }

    public updateComparisonHistoryData(model: TIComparisonHistoryDataModel) {
        if (!model?.points?.length) {
            return
        }

        const existingComparsionSymbolData = this.tiChartSeries.tiComparisionsData.find((x: TIComparisonDataModel) => x.symbol == model.symbol);
        const existingSerie =  this.tiChartSeries.highStockChart.series.find((x) => x.name == 'comparison-series-' + model.symbol);

        if (!existingComparsionSymbolData || !existingSerie) {
            return;
        }

        this.updatingDataPoint = true;
        for (let i = model.points.length - 1; i >= 0; i--) {
            const point = model.points[i];
            if (!existingComparsionSymbolData.points.some((x: TIChartPointModel) => x.xValue == point.xValue)) {
                existingComparsionSymbolData.points.unshift(point);
            }
        }

        this.updatingDataPoint = false;

        // const chartStyle = this.tiChartSeries.chartStyleSettings;
        // if (!chartStyle?.isComparisonSymbolPercentageMode) {
        //     return;
        // }

        this.buildComparisonsComponents(0, false);
        // this.setRatio(existingComparsionSymbolData, this.firstVisibleXValue);
        // const dataPoints = TIComparisonDataPointToHighChartDataPointMapper.map(existingComparsionSymbolData, this.tiChartSeries);

        // existingSerie.setData(dataPoints.points, true, false);
    }
    
    public getPercentageAnnotationText(tiDataPoint: TIChartPointModel | null, ratio?: number): string {
        const percentage = this.getPercentage(tiDataPoint, ratio);

        let annotationText = FormatHelpers.numberToString(Math.abs(percentage)) + "%";
        if (annotationText == '0.00%') {
            return annotationText;
        }

        if (percentage > 0) {
            annotationText = '+' + annotationText;
        } else if (percentage < 0) {
            annotationText = '-' + annotationText;
        }

        return annotationText;
    }

    public getCurrentDisplayValue(model: TIComparisonDataModel, xValue: number): CurrentDisplayValueResult {
        const comparisonDataPoints = model.points;
        const point = comparisonDataPoints.find((x: TIChartPointModel) => x.xValue == xValue) ?? null;

        const value = (point?.close ?? 0) * model.ratio;
        const valueText = FormatHelpers.numberToString(value);
        const percentageText = this.getPercentageAnnotationText(point, model.ratio);
        if (this.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode) {
            return {
                value: percentageText,
                percentageChange: percentageText
            };
        }

        return {
            value: valueText,
            percentageChange: percentageText
        };
    }

    public getPercentage(tiDataPoint: TIChartPointModel | null, ratio?: number): number {
        const anchorDataPoint = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == this.firstVisibleXValue);
        if (!tiDataPoint || !tiDataPoint.close || !anchorDataPoint || !anchorDataPoint.close) {
            return 0;
        }

        const close = tiDataPoint.close * (ratio ?? 1);
        const percentage = ((close / anchorDataPoint.close) - 1) * 100;
        return percentage;
    }

    public setRatio(model: TIComparisonDataModel, xValue: number | null) {
        let ratio = 1.00;
        // let currentXValue: number | null = null;
        const matchingPrimaryComparison = this.findMatchingPrimaryComparisonPointPair(model.points, xValue);

        if (matchingPrimaryComparison) {
            ratio = matchingPrimaryComparison.primaryDataPoint.close / matchingPrimaryComparison.matchingComparisonDataPoint.close;
            // currentXValue = matchingPrimaryComparison.primaryDataPoint.xValue;
        } else {
            TraceLoggingHelpers.log(`matchingPrimaryComparison is null`);
        }


        model.ratio = ratio;
    }

    private findMatchingPrimaryComparisonPointPair(comparionDataPoints: Array<TIChartPointModel>, xValue?: number | null): PrimaryComparisonPointPair | null {
        const tiDataPoints = this.tiChartSeries.tiChartPoints;
        const isPercentageMode = this.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode;

        if (xValue) {
            const startIndex = tiDataPoints.findIndex(x => x.xValue == xValue);
            for (let i = startIndex; i < tiDataPoints.length; i++) {
                let primaryDataPoint = tiDataPoints[i];
                let matchingComparisonDataPoint: any = null;
                if (isPercentageMode) {
                    matchingComparisonDataPoint = comparionDataPoints.find(x => x.xValue == primaryDataPoint.xValue);
                }

                if (!matchingComparisonDataPoint) {
                    matchingComparisonDataPoint = comparionDataPoints.find(x => x.startTimeT == primaryDataPoint.startTimeT);
                }

                if (matchingComparisonDataPoint) {
                    return {
                        primaryDataPoint: primaryDataPoint,
                        matchingComparisonDataPoint: matchingComparisonDataPoint
                    };
                }
            }
        }

        const initialDataPoints = tiDataPoints.sort((a, b) => a.startTimeT - b.startTimeT);
        for (const primaryDataPoint of initialDataPoints) {
            var matchingComparisonDataPoint = comparionDataPoints.find(x => x.startTimeT == primaryDataPoint.startTimeT);
            if (matchingComparisonDataPoint) {
                return {
                    primaryDataPoint: primaryDataPoint,
                    matchingComparisonDataPoint: matchingComparisonDataPoint
                };
            } else {
                continue;
            }
        }

        return null;
    }

    private getComponents()  {
        if (!this.comparisonSymbolLegend) {
            const components = this.tiChartSeries.base.getComponentsByType(ComparisonSymbolLegendsComponent);
            if (components?.length) {
                this.comparisonSymbolLegend = components[0] as ComparisonSymbolLegendsComponent;
            }
        }

        if (!this.comparisonSymbolsLastPrice) {
            const components = this.tiChartSeries.base.getComponentsByType(ComparisonSymbolsLastPriceComponent);
            if (components?.length) {
                this.comparisonSymbolsLastPrice = components[0] as ComparisonSymbolsLastPriceComponent;
            }
        }
    }
}